import { Layout as LayoutComponent } from "../utils/local-antd";

import { faChevronCircleLeft } from "@fortawesome/free-solid-svg-icons/faChevronCircleLeft";
import { faChevronCircleRight } from "@fortawesome/free-solid-svg-icons/faChevronCircleRight";

import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, ScrollRestoration } from "react-router-dom";
import styled, { createGlobalStyle } from "styled-components";

import { useTheme } from "../ThemeContext";
import { AtiraIcon } from "../components/AtiraIcon";
import { DailyStatsFooter } from "../components/DailyStatsFooter";
import { Flex } from "../components/Flex";
import { SideMenu } from "../components/SideMenu";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { useIsViewportBlocked } from "../hooks/useIsViewportBlocked";
import useWindowDimensions from "../hooks/useWindowDimensions";
import i18n from "../i18n";
import { appSliceSelectors } from "../redux/app/app.selector";
import { appActions } from "../redux/app/app.slice";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { userSliceSelectors } from "../redux/user/user.selector";
import { Breakpoints } from "../theme/Breakpoints";
import { Spacing } from "../theme/Spacing";
import { MobileBlockingModal } from "./MobileBlockingModal";
import { UserDisabledNotificationModal } from "./UserDisabledNotificationModal";
import { InviteNeedActionModal } from "./permissions/components/modals/InviteNeedActionModal";
import { Header } from "./shared/Header";
import { ChatWidget } from "../components/chat-widget/ChatWidget";
import { PSAUnreadBanner } from "../components/psa/PSAUnreadBanner";
import isEmpty from "lodash/isEmpty";
import { GlobalDrawers } from "../components/GlobalDrawers";
import { Rounded } from "../theme/Rounded";
import { AppLangs } from "../model/shared/enum/AppLangs.enum";
import { Text } from "../components/Text";
import { TestIDs } from "../tests/types/testIds";
import { SettingsInitialPreferencesModal } from "./settings/components/SettingsInitialPreferencesModal";
import { isImpersonation, isSupportSession } from "../redux/store-utils";

const IMPERSONATION_BANNER_HEIGHT = "1.75rem";

const HeaderWrapper = styled(Flex)`
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 100;
`;

const ImpersonationBanner = styled(Flex)`
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: ${IMPERSONATION_BANNER_HEIGHT};
  padding: 0 ${Spacing.m};
  background-color: ${({ theme }) => theme.warning};
  color: ${({ theme }) => theme.black};
  border-bottom: ${({ theme }) => `1px solid ${theme.main}22`};
`;

const ImpersonationText = styled(Text)`
  color: ${({ theme }) => theme.black};
  width: 100%;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.8125rem;
`;

const OutletWrapper = styled(Flex)<{
  $isTherePSA: boolean;
  $isImpersonating: boolean;
}>`
  justify-content: space-between;
  width: 100%;
  // 3.25rem = header height, 6rem = PSAUnreadBanner height, impersonation banner height = tiny status strip, Spacing.xl = daily info footer height
  height: ${({ $isTherePSA, $isImpersonating }) =>
    `calc(100% - (3.25rem + ${$isTherePSA ? "6rem" : "0rem"} + ${$isImpersonating ? IMPERSONATION_BANNER_HEIGHT : "0rem"} + ${Spacing.xl}))`};
  flex-direction: column;
  overflow-y: auto;
  margin-top: ${({ $isTherePSA, $isImpersonating }) =>
    `calc(3.25rem + ${$isTherePSA ? "6rem" : "0rem"} + ${$isImpersonating ? IMPERSONATION_BANNER_HEIGHT : "0rem"})`};
  background-color: ${({ theme }) => theme.sub};
  padding-bottom: ${Spacing.l};
`;

/* Wraps the routed page so the shell stays mounted while the viewport is
   blocked (see the render below). No min-width here on purpose: a global one
   stacks a second, direction-confused horizontal scroll on top of whatever the
   page already scrolls (RTL especially). A page that needs a floor sets it on
   its own container. */
const OutletFrame = styled(Flex)`
  flex: 1;
  flex-direction: column;
  width: 100%;
  min-height: 0;
`;

const StyledSiderLayout = styled(LayoutComponent.Sider)`
  width: 15.5rem;
  overflow: auto;
  height: 100vh;
  height: 100dvh;

  /* Changing the width values here requires updating the:
    - The Container's 'width' and 'inset-inline-start' in DailyStatsFooter.tsx
    - '.ant-layout-sider' class's width values in SideMenu.tsx
  */
  .ant-layout-sider-trigger {
    width: 190px !important;
  }

  &.ant-layout-sider-collapsed .ant-layout-sider-trigger {
    width: 60px !important;
  }

  .ant-layout-sider-trigger svg {
    rotate: ${i18n.language === AppLangs.AR ? "180deg" : "0"};
  }
  .ant-layout-sider-trigger {
    background-color: ${({ theme }) => theme.main} !important;
  }
`;

export const Layout: React.FC = () => {
  const storedValue = useAppSelector(appSliceSelectors.selectSideMenuCollapsed);
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser);
  const possibleLastUnreadPSAs =
    useAppSelector(userSliceSelectors.selectLastUnreadPSAs) || [];

  const [collapsed, setCollapsed] = useState(Boolean(storedValue));

  const dispatch = useAppDispatch();
  const { theme } = useTheme();

  const { t } = useTranslation();
  useDocumentTitle(t("app.name").toUpperCase());
  const isUserDisabled = user?.enabled === false;
  const { blocked, width, dismiss } = useIsViewportBlocked();

  // Below the desktop breakpoint the sider is pinned collapsed so it stops
  // eating ~190px the narrow layout can't spare. The user's own collapse
  // preference is left untouched for when the window grows back.
  const forceCollapsed = width < Number(Breakpoints.DESKTOP.replace("px", ""));
  const effectiveCollapsed = collapsed || forceCollapsed;

  const onCollapse = (newCollapsed: boolean) => {
    if (forceCollapsed) {
      return;
    }
    setCollapsed(newCollapsed);
    dispatch(appActions.setSideMenuCollapsed(newCollapsed));
  };

  if (isUserDisabled) {
    return <UserDisabledNotificationModal />;
  }

  return (
    <Fragment>
      {blocked ? <MobileBlockingModal onContinue={dismiss} /> : null}
      <LayoutComponent style={{ width: "100%", height: "100%" }}>
        <HeaderWrapper>
          {isImpersonation() ? (
            <ImpersonationBanner
              dataTestId={TestIDs.general.impersonation.banner}
            >
              <ImpersonationText fontWeight="bold">
                {t(
                  isSupportSession()
                    ? "impersonation.supportBannerText"
                    : "impersonation.bannerText",
                  {
                    userName:
                      user?.name ||
                      user?.email ||
                      t("impersonation.linkedAccount"),
                  },
                )}
              </ImpersonationText>
            </ImpersonationBanner>
          ) : null}

          <PSAUnreadBanner />
          <Header />
        </HeaderWrapper>
        <StyledSiderLayout
          collapsible={!forceCollapsed}
          collapsed={effectiveCollapsed}
          onCollapse={onCollapse}
          style={{ backgroundColor: theme.main }}
          trigger={
            <AtiraIcon
              icon={
                effectiveCollapsed ? faChevronCircleRight : faChevronCircleLeft
              }
              size="xl"
            />
          }
        >
          <SideMenu collapsed={effectiveCollapsed} />
        </StyledSiderLayout>

        <OutletWrapper
          id="outlet-wrapper"
          $isTherePSA={!isEmpty(possibleLastUnreadPSAs)}
          $isImpersonating={isImpersonation()}
        >
          <ScrollRestoration />
          <OutletFrame>
            {/* Keep the shell (header, sider, footer) mounted while blocked so
                crossing the threshold doesn't flash the whole app, but don't
                mount page content at widths it was never built for. */}
            {blocked ? null : <Outlet />}
          </OutletFrame>
          <DailyStatsFooter sideMenuCollapsed={effectiveCollapsed} />
          {user?._id ? <ChatWidget /> : null}
          <InviteNeedActionModal />
          <SettingsInitialPreferencesModal />
        </OutletWrapper>
      </LayoutComponent>

      <GlobalDrawers />
    </Fragment>
  );
};
